import { v4 as uuidv4 } from 'uuid';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import { ElementType } from '@goldcast/player-config/src/types/Element/Type';
import { CompositionConfig } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/CompositionConfig';
import { AudioElement } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Element/AudioElement';
import { Element } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Element/Element';
import {
  ImageElement,
  ImageStyleProperties
} from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Element/ImageElement';
import {
  LottieElement,
  LottieName,
  LottieStyleProperties
} from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Element/LottieElement';
import {
  TextElement,
  TextElementTextStyle,
  TextElementTransitions
} from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Element/TextElement';
import {
  VideoElement,
  VideoStyleProperties
} from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Element/VideoElement';
import { ElementsTrack } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Track/ElementsTrack';
import { Track } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Track/Track';
import { TrackType } from '@goldcast/player-config/src/types/Track/Type';
import { isVideoElement } from '@goldcast/player-config/src/utils/elementUtils';
import {
  DEFAULT_AUDIO_DURATION,
  DEFAULT_CONTAINER_STYLE,
  DEFAULT_IMAGE_DURATION,
  DEFAULT_IMAGE_STYLE,
  DEFAULT_LOTTIE_CONTAINER_STYLE,
  DEFAULT_LOTTIE_STYLE,
  DEFAULT_VIDEO_CONTAINER_STYLE,
  DEFAULT_VIDEO_DURATION,
  DEFAULT_VIDEO_STYLE
} from './constants';
import {
  ElementLanes,
  ElementResizeSide
} from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/TimelineTrackElementLanes/types';
import {
  ElementResizeSideEnum,
  MINIMUM_TRACK_TIME_DIFFERENCE
} from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/TimelineTrackElementLanes/constants';
import { UploadedAsset } from '@/components/molecules/MultiAssetUploader/types';
import { saveClipChanges, updateClipWithId } from '@/stores/clip';
import { clipVersionStore } from '@/stores/clipVersionStore';
import { VERSION_EDIT_TYPE } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtils/constants';

export const LOTTIE_OPTIONS: { value: LottieName; label: string }[] = [
  { value: 'crazy_swirl', label: 'Crazy Swirl' },
  { value: 'cloud', label: 'Cloud' },
  { value: 'circle_swirl', label: 'Circle Swirl' },
  { value: 'fire', label: 'Fire' },
  { value: 'fade', label: 'Fade' }
];

export const isAudioElement = (element: Element): element is AudioElement => {
  return element.type === ElementType.AUDIO;
};

export const isTextElement = (element: Element): element is TextElement => {
  return element.type === ElementType.TEXT;
};

export const isLottieElement = (element: Element): element is LottieElement => {
  return element.type === ElementType.LOTTIE;
};

export const hasElementDuration = (element: Element): element is AudioElement | VideoElement => {
  return isAudioElement(element) || isVideoElement(element);
};

export const getElementDuration = (element: Element): number => {
  if (hasElementDuration(element)) {
    return element.details.duration || 0;
  }
  return 0;
};

export const isElementLooping = (element: Element): boolean => {
  return isAudioElement(element) && Boolean(element.details.loop);
};

export const generateMotionGraphicName = (lottieName: LottieName): string => {
  const option = LOTTIE_OPTIONS.find(opt => opt.value === lottieName);
  const displayName = option?.label || lottieName;
  return `Graphic - ${displayName}`;
};

export const createImageElement = ({
  currentFrame,
  asset,
  duration = DEFAULT_IMAGE_DURATION,
  fps
}: {
  currentFrame: number;
  asset: UploadedAsset;
  duration?: number;
  fps: number;
}): ImageElement => ({
  id: uuidv4(),
  name: asset.filename,
  type: ElementType.IMAGE,
  fromFrame: currentFrame,
  toFrame: currentFrame + duration * fps,
  details: {
    src: asset.url,
    altText: asset.filename
  },
  style: DEFAULT_IMAGE_STYLE as Partial<ImageStyleProperties>,
  containerStyle: DEFAULT_CONTAINER_STYLE as Partial<ImageStyleProperties>
});

export const createVideoElementConfig = ({
  currentFrame,
  asset,
  duration = DEFAULT_VIDEO_DURATION,
  fps
}: {
  currentFrame: number;
  asset: UploadedAsset;
  duration: number;
  fps: number;
}): VideoElement => ({
  id: uuidv4(),
  name: asset.filename,
  type: ElementType.VIDEO,
  fromFrame: currentFrame,
  toFrame: currentFrame + duration * fps,
  details: {
    src: asset.url,
    altText: asset.filename,
    duration,
    volume: [
      {
        value: 100,
        fromFrame: 0,
        toFrame: duration * fps
      }
    ]
  },
  style: DEFAULT_VIDEO_STYLE as Partial<VideoStyleProperties>,
  containerStyle: DEFAULT_VIDEO_CONTAINER_STYLE as Partial<VideoStyleProperties>,
  trim: [],
  isOriginalVideo: false
});

export const createAudioElement = ({
  currentFrame,
  asset,
  duration = DEFAULT_AUDIO_DURATION,
  fps
}: {
  currentFrame: number;
  asset: UploadedAsset;
  duration?: number;
  fps: number;
}): AudioElement => {
  return {
    id: uuidv4(),
    name: asset.name || asset.filename,
    type: ElementType.AUDIO,
    fromFrame: currentFrame,
    toFrame: currentFrame + duration * fps,
    details: {
      src: asset.url,
      duration,
      volume: [
        {
          value: 100,
          fromFrame: 0,
          toFrame: duration * fps
        }
      ],
      fadeIn: 0,
      fadeOut: 0
    },
    trim: []
  };
};

export const createLottieElement = ({
  id,
  name,
  fromFrame,
  toFrame,
  lottie_name = 'crazy_swirl',
  hex_colors = ['FF0000', '03FF00', 'FF7400'],
  containerStyle = DEFAULT_LOTTIE_CONTAINER_STYLE,
  style = DEFAULT_LOTTIE_STYLE
}: {
  id: string;
  name?: string;
  fromFrame: number;
  toFrame: number;
  lottie_name?: LottieName;
  hex_colors?: string[];
  containerStyle?: Partial<LottieStyleProperties>;
  style?: Partial<LottieStyleProperties>;
}): LottieElement => ({
  id,
  name: name || generateMotionGraphicName(lottie_name),
  type: ElementType.LOTTIE,
  fromFrame,
  toFrame,
  lottie_name,
  hex_colors,
  containerStyle,
  style
});

export const createTextElement = ({
  id,
  name,
  fromFrame,
  toFrame,
  details,
  containerStyle,
  style,
  transitions
}: {
  id: string;
  name: string;
  fromFrame: number;
  toFrame: number;
  details: { text: string; font: { name: string; location: string }; areCaptionsHidden?: boolean };
  containerStyle: any;
  style: TextElementTextStyle;
  transitions: TextElementTransitions;
}): TextElement => ({
  id,
  name,
  type: ElementType.TEXT,
  fromFrame,
  toFrame,
  details,
  containerStyle,
  style,
  transitions
});

export const createNewElementsTrack = (element: Element): ElementsTrack => {
  const trackId = uuidv4();
  return {
    id: trackId,
    name: `Track - ${trackId}`,
    type: TrackType.ELEMENTS,
    hideTrack: { inTimeline: false, inPlayer: false },
    elements: [element]
  };
};

export const insertElementIntoNewNonOverlappingTrack = (tracks: ElementsTrack[], element: Element): ElementsTrack[] => {
  for (const track of tracks) {
    const hasOverlap = track.elements.some(existing => hasTimeOverlap(existing, element));
    if (!hasOverlap) {
      return tracks.map(t => (t.id === track.id ? { ...t, elements: [...t.elements, element] } : t));
    }
  }

  return [createNewElementsTrack(element), ...tracks];
};

export const updatePlayerConfigWithTracks = (playerConfig: CompositionConfig, updatedTracks: ElementsTrack[]) => ({
  ...playerConfig,
  playerConfig: {
    ...playerConfig.playerConfig,
    tracks: updatedTracks
  }
});

export const updateClipData = (clipData: Clip, updatedPlayerConfig: CompositionConfig) => ({
  ...clipData,
  asset_metadata: {
    ...clipData.asset_metadata,
    player_config: updatedPlayerConfig
  }
});

export const hasTimeOverlap = (
  element1: { fromFrame: number; toFrame: number },
  element2: { fromFrame: number; toFrame: number }
): boolean => {
  return !(element1.toFrame <= element2.fromFrame || element2.toFrame <= element1.fromFrame);
};

export const generateElementLanes = (tracks: Track[]): ElementLanes => {
  const elementTracks = tracks.filter(track => track.type === TrackType.ELEMENTS);

  const elementLanes: ElementLanes = elementTracks.map(track => {
    return track.elements || [];
  });

  return elementLanes.filter(lane => lane.length > 0);
};

export const createElementTracksFromLanes = (elementLanes: ElementLanes): ElementsTrack[] => {
  return elementLanes.map(lane => ({
    id: uuidv4(),
    name: `Track - ${uuidv4()}`,
    type: TrackType.ELEMENTS as const,
    hideTrack: { inTimeline: false, inPlayer: false },
    elements: lane
  }));
};

export const updatePlayerConfigWithElementLanes = (
  playerConfig: CompositionConfig,
  elementLanes: ElementLanes,
  clipData: Clip,
  clipId: string
) => {
  const currentTracks = playerConfig?.playerConfig?.tracks ?? [];
  const otherTracks = currentTracks.filter(track => track.type !== TrackType.ELEMENTS);

  const updatedElementTracks = createElementTracksFromLanes(elementLanes);
  const combinedTracks = [...otherTracks, ...updatedElementTracks];

  const updatedConfig = updatePlayerConfigWithTracks(playerConfig, combinedTracks as ElementsTrack[]);
  const updatedClipData = updateClipData(clipData, updatedConfig);

  updateClipWithId(clipId, updatedClipData);
  // Timeline element move and resize both commit here. Reported alongside the save so a
  // drag that changed nothing — and returns before reaching this — creates no version.
  clipVersionStore.emitEdit(clipId, VERSION_EDIT_TYPE.OVERLAY_UPDATE);
  saveClipChanges({ clipId, shouldGenerateThumbnail: false, shouldAddToHistory: true });

  return updatedClipData;
};

export const updateElementFrames = ({
  element,
  dragOffset,
  dragSide,
  initialFrames,
  fps
}: {
  element: Element;
  dragOffset: number;
  dragSide: ElementResizeSide;
  initialFrames: { fromFrame: number; toFrame: number };
  fps: number;
}): Element => {
  if (isLottieElement(element) && dragSide !== ElementResizeSideEnum.CENTER) {
    return element;
  }

  const offset = dragOffset * fps;
  const minDuration = MINIMUM_TRACK_TIME_DIFFERENCE * fps;

  const elementDuration = getElementDuration(element) * fps;
  const isLooping = isElementLooping(element);
  const updateFrames = {
    [ElementResizeSideEnum.CENTER]: () => {
      const duration = initialFrames.toFrame - initialFrames.fromFrame;
      const fromFrame = Math.max(0, initialFrames.fromFrame + offset);
      return { fromFrame, toFrame: fromFrame + duration };
    },
    [ElementResizeSideEnum.START]: () => {
      // Calculate new fromFrame position with bounds checking
      const newFromFrame = Math.max(0, Math.min(initialFrames.fromFrame + offset, initialFrames.toFrame - minDuration));
      const toFrame = initialFrames.toFrame;
      const newDuration = toFrame - newFromFrame;

      // Check if the new duration exceeds the original element duration
      const exceedsOriginalDuration = elementDuration > 0 && elementDuration < newDuration;
      const shouldRejectResize = exceedsOriginalDuration && !isLooping;

      if (shouldRejectResize) {
        // Reject resize - limit to original element duration
        const minFromFrame = Math.max(0, initialFrames.toFrame - elementDuration);
        return { fromFrame: minFromFrame, toFrame: initialFrames.toFrame };
      }

      // Accept resize
      return { fromFrame: newFromFrame, toFrame };
    },
    [ElementResizeSideEnum.END]: () => {
      const fromFrame = initialFrames.fromFrame;
      // Calculate new toFrame position with minimum duration constraint
      const newToFrame = Math.max(initialFrames.fromFrame + minDuration, initialFrames.toFrame + offset);
      const newDuration = newToFrame - fromFrame;

      // Check if the new duration exceeds the original element duration
      const exceedsOriginalDuration = elementDuration > 0 && elementDuration < newDuration;
      const shouldRejectResize = exceedsOriginalDuration && !isLooping;

      if (shouldRejectResize) {
        // Reject resize - limit to original element duration
        const maxToFrame = initialFrames.fromFrame + elementDuration;
        return { fromFrame: initialFrames.fromFrame, toFrame: maxToFrame };
      }

      // Accept resize
      return { fromFrame, toFrame: newToFrame };
    }
  };

  const { fromFrame, toFrame } = updateFrames[dragSide]();
  return { ...element, fromFrame, toFrame, metadata: undefined };
};

export const updateElementLanesOnHorizontalDrag = (
  currentLanes: ElementLanes,
  updatedElement: Element
): ElementLanes => {
  const newLanes = currentLanes.map(lane => [...lane]);

  let currentLaneIndex = -1;
  let elementIndexInLane = -1;

  for (let i = 0; i < newLanes.length; i++) {
    const index = newLanes[i].findIndex(el => el.id === updatedElement.id);
    if (index !== -1) {
      currentLaneIndex = i;
      elementIndexInLane = index;
      break;
    }
  }

  if (currentLaneIndex === -1) {
    return currentLanes;
  }

  newLanes[currentLaneIndex][elementIndexInLane] = updatedElement;

  const otherElementsInLane = newLanes[currentLaneIndex].filter(el => el.id !== updatedElement.id);
  const hasOverlap = otherElementsInLane.some(existing => hasTimeOverlap(existing, updatedElement));

  if (hasOverlap) {
    newLanes[currentLaneIndex] = newLanes[currentLaneIndex].filter(el => el.id !== updatedElement.id);
    newLanes.splice(currentLaneIndex + 1, 0, [updatedElement]);
  }

  return newLanes.filter(lane => lane.length > 0);
};

export const moveElementToLane = (
  elementLanes: ElementLanes,
  elementId: string,
  targetLaneIndex: number,
  snapPosition: 'top' | 'bottom'
): ElementLanes => {
  const newLanes = elementLanes.map(lane => lane.filter(el => el.id !== elementId));
  const element = elementLanes.flat().find(el => el.id === elementId);

  if (!element) {
    return elementLanes;
  }

  if (targetLaneIndex >= newLanes.length) {
    newLanes.push([element]);
    return newLanes.filter(lane => lane.length > 0);
  }

  const targetLane = newLanes[targetLaneIndex] || [];
  const hasOverlap = targetLane.some(existing => hasTimeOverlap(existing, element));

  if (hasOverlap) {
    const insertionIndex = snapPosition === 'top' ? targetLaneIndex : targetLaneIndex + 1;
    newLanes.splice(insertionIndex, 0, [element]);
  } else {
    if (snapPosition === 'top') {
      newLanes[targetLaneIndex] = [element, ...targetLane];
    } else {
      newLanes[targetLaneIndex] = [...targetLane, element];
    }
  }

  return newLanes.filter(lane => lane.length > 0);
};

export const updateElementProperties = (
  playerConfig: CompositionConfig,
  elementId: string,
  updates: {
    style?: ImageStyleProperties | VideoStyleProperties;
    containerStyle?: ImageStyleProperties | VideoStyleProperties;
  }
) => {
  const updatedTracks = playerConfig.playerConfig?.tracks.map((track: any) => {
    if (track.type !== TrackType.ELEMENTS || !Array.isArray(track.elements)) {
      return track;
    }

    const updatedElements = track.elements.map(element => {
      if (element.id !== elementId) {
        return element;
      }

      return {
        ...element,
        ...(updates.style && {
          style: {
            ...element.style,
            ...updates.style
          }
        }),
        ...(updates.containerStyle && {
          containerStyle: {
            ...element.containerStyle,
            ...updates.containerStyle
          }
        }),
        metadata: undefined
      };
    });

    return {
      ...track,
      elements: updatedElements
    };
  });

  return {
    ...playerConfig,
    playerConfig: {
      ...playerConfig.playerConfig,
      tracks: updatedTracks
    }
  };
};

export const updateElementStyles = (
  playerConfig: CompositionConfig,
  elementId: string,
  newStyles: ImageStyleProperties | VideoStyleProperties
) => updateElementProperties(playerConfig, elementId, { style: newStyles });

export const updateElementPosition = (
  playerConfig: CompositionConfig,
  elementId: string,
  elementStyles: {
    containerStyle: ImageStyleProperties | VideoStyleProperties;
    style: ImageStyleProperties | VideoStyleProperties;
  }
) => updateElementProperties(playerConfig, elementId, elementStyles);

export const updateElementDetails = (
  playerConfig: CompositionConfig,
  elementId: string,
  details: {
    [key: string]: any;
  },
  frameUpdates?: {
    toFrame?: number;
    fromFrame?: number;
  },
  name?: string
): CompositionConfig => {
  const updatedTracks: Track[] = playerConfig.playerConfig?.tracks.map((track: any) => {
    if (track.type !== TrackType.ELEMENTS || !Array.isArray(track.elements)) {
      return track;
    }

    const updatedElements: Element[] = track.elements.map(element => {
      if (element.id !== elementId) {
        return element;
      }

      return {
        ...element,
        details: {
          ...element.details,
          ...details
        },
        ...(frameUpdates?.fromFrame !== undefined && { fromFrame: frameUpdates.fromFrame }),
        ...(frameUpdates?.toFrame !== undefined && { toFrame: frameUpdates.toFrame }),
        ...(name !== undefined && { name })
      };
    });

    return {
      ...track,
      elements: updatedElements
    };
  });

  return {
    ...playerConfig,
    playerConfig: {
      ...playerConfig.playerConfig,
      tracks: updatedTracks
    }
  };
};

export function deleteTrackElement(config: CompositionConfig, elementId: string): CompositionConfig {
  const updatedTracks = config.playerConfig?.tracks.map((track: any) => {
    if (track.type !== 'ELEMENTS' || !Array.isArray(track.elements)) {
      return track;
    }

    const filteredElements = track.elements.filter((element: any) => element.id !== elementId);

    return {
      ...track,
      elements: filteredElements
    };
  });
  return {
    ...config,
    playerConfig: {
      ...config.playerConfig,
      tracks: updatedTracks
    }
  };
}

export const findTrackElementById = (clipData: Clip, elementId: string, elementType?: ElementType) => {
  const tracks = clipData?.asset_metadata?.player_config?.playerConfig?.tracks;
  if (!tracks) {
    return null;
  }

  for (const track of tracks) {
    if (track.type === TrackType.ELEMENTS && track.elements) {
      const element = track.elements.find(el => el.id === elementId && (!elementType || el.type === elementType));
      if (element) {
        return element;
      }
    }
  }
  return null;
};
